// src/components/dashboard/Mainscreen.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';

// Layout
import SignInHeader from '../auth/SigninHeader';
import SideNavigation from '../signIndropdown/SideNavigation';

// Dashboard components
import RecommendedCard from './RecommendedCard';
import AppItem         from './AppItem';
import FilterBar       from './FilterBar';

// Shared components
import FloatingActionButton from '../shared/FloatingActionButton';

// NEW: Import the master InspectionBotPage instead of individual components
import InspectionBotPage from '../inspectionBot/InspectionBotPage';

const Mainscreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isDarkMode, setIsDarkMode]       = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [errorMessage, setErrorMessage]   = useState('');
  const [user, setUser]                   = useState(null);
  const [isLoading, setIsLoading]         = useState(true);

  // Determine if we're in inspection bot flow
  const isInspectionBotFlow = location.pathname.includes('/template-viewer') || 
                             location.pathname.includes('/record-types') ||
                             location.pathname.includes('/triggerlocation') ||
                             location.pathname.includes('/taskselection') ||
                             location.pathname.includes('/inspection-result') ||
                             location.pathname.includes('/contact-types') ||
                             location.pathname.includes('/summary');

  // 1. Authentication and user data loading
  useEffect(() => {
    const initializeUser = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error('Error parsing stored user:', e);
          localStorage.removeItem('user');
        }
      }

      if (!token) {
        setErrorMessage('Please sign in to access this page.');
        navigate('/signin');
        return;
      }

      try {
        const response = await axios.get('http://localhost:5000/api/protected', {
          headers: { Authorization: token }
        });
        
        if (response.data.success) {
          setAuthenticated(true);
        }
      } catch (error) {
        console.error('Authentication failed:', error);
        setErrorMessage('Authentication failed. Please sign in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/signin');
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [navigate]);

  // 2. Load dark-mode preference
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(saved === 'dark' || (!saved && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  // 3. Handle clicking on a bot card
  const handleBotClick = (botId) => {
    if (botId === 'inspection-bot') {
      // Clear any previous inspection bot data
      const keysToRemove = [
        'selectedTemplate', 'group', 'type', 'subtype', 'category',
        'selectedG6Type', 'selectedInspectionResult', 'selectedContactType'
      ];
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Navigate to template viewer
      navigate('template-viewer');
    } else {
      console.log(`Bot clicked: ${botId}`);
    }
  };

  // Sample data with fixed structure
  const recommendedApps = [
    { 
      title: 'Communication Bot', 
      description: 'Trigger communications based on any criteria', 
      meta: 'Used by 12 team members' 
    },
    { 
      title: 'Workflow Bot', 
      description: 'Create complex automated process flows', 
      tag: 'NEW' 
    },
    { 
      title: 'Validation Bot', 
      description: 'Put checks in place to ensure criteria are met', 
      tag: 'POPULAR' 
    },
    { 
      title: 'Inspection Bot', 
      description: 'Automate inspection-based communications', 
      tag: 'NEW', 
      botId: 'inspection-bot' 
    },
  ];

  const allApps = [
    { title: 'Communication Bot', tags: ['Building','Planning'], usage: '542 uses this month' },
    { title: 'Workflow Bot', tags: ['All modules'], usage: '127 uses this month' },
    { title: 'Validation Bot', tags: ['Permits','Inspections'], usage: '285 uses this month' },
    { title: 'Event Bot', tags: ['Code Enforcement'], usage: '98 uses this month' },
    { title: 'Inspection Bot', tags: ['Agency'], usage: '19 uses this month' },
  ];

  // Show loading state
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDarkMode ? 'bg-main-dark text-gray-300' : 'bg-main-light text-gray-600'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (errorMessage && !authenticated) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        isDarkMode ? 'bg-main-dark' : 'bg-main-light'
      }`}>
        <div className="text-center p-6 rounded-xl bg-red-50 text-red-600 max-w-md">
          <div className="mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <p className="text-lg font-medium">{errorMessage}</p>
          <button
            onClick={() => navigate('/signin')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // If we're in the inspection bot flow, render ONLY the InspectionBotPage
  if (isInspectionBotFlow) {
    return <InspectionBotPage />;
  }

  // Otherwise, render the normal dashboard layout with side navigation
  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-main-dark text-gray-300' : 'bg-main-light text-gray-600'
    }`}>
      
      {/* Header only - no top navigation */}
      <SignInHeader
        user={user}
        isLoggedIn={authenticated}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Side Navigation */}
      <SideNavigation
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      {/* Main dashboard content - adjusted for side navigation */}
      <main className="pt-[70px] pl-20 pb-12 px-4 min-h-screen">
        <div className="max-w-7xl mx-auto px-4">
          <Routes>
            {/* Dashboard "Home" view */}
            <Route index element={
              <div className="space-y-8 py-8">
                {/* Page Title */}
                <div className="mb-8">
                  <h1 className={`text-3xl font-bold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Dashboard
                  </h1>
                  <p className={`text-lg mt-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Welcome back! Manage your bots and automations.
                  </p>
                </div>

                {/* Recommended Apps Section */}
                <section>
                  <div className="mb-6">
                    <h2 className={`text-sm font-semibold tracking-wider uppercase ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Recommended for You
                    </h2>
                    <div className={`h-px bg-gradient-to-r from-purple-500 to-transparent mt-2 ${
                      isDarkMode ? 'opacity-30' : 'opacity-20'
                    }`}></div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {recommendedApps.map((app, idx) => (
                      <RecommendedCard
                        key={idx}
                        {...app}
                        isDarkMode={isDarkMode}
                        onClick={() => app.botId && handleBotClick(app.botId)}
                      />
                    ))}
                  </div>
                </section>

                {/* All Apps Section */}
                <section>
                  <div className="mb-6">
                    <h2 className={`text-sm font-semibold tracking-wider uppercase ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      All Apps
                    </h2>
                    <div className={`h-px bg-gradient-to-r from-purple-500 to-transparent mt-2 ${
                      isDarkMode ? 'opacity-30' : 'opacity-20'
                    }`}></div>
                  </div>
                  
                  <FilterBar isDarkMode={isDarkMode} />
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                    {allApps.map((app, idx) => (
                      <AppItem 
                        key={idx} 
                        {...app} 
                        isDarkMode={isDarkMode}
                        onUse={() => console.log(`Using ${app.title}`)}
                      />
                    ))}
                  </div>
                </section>
              </div>
            }/>

            {/* 
              NOTE: All inspection bot routes are now handled by InspectionBotPage
              We don't need individual Route definitions here anymore since
              InspectionBotPage handles the step routing internally
            */}
          </Routes>
        </div>
      </main>

      {/* Floating Action Button - Only show on main dashboard */}
      <FloatingActionButton 
        isDarkMode={isDarkMode}
        onClick={() => console.log('Create new bot')}
      />
    </div>
  );
};

export default Mainscreen;